import { useHandler } from "@redotech/react-util/hook";
import { assertNever } from "@redotech/util/type";
import classnames from "classnames";
import { memo } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { RedoRadioButton } from "../radio/redo-radio-button";
import { RedoCheckbox, RedoCheckboxSize } from "./redo-checkbox";
import * as css from "./redo-checkbox-card.module.css";

export const checkboxCardInputTypes = ["radio", "checkbox"] as const;
export type CheckboxCardInputType = (typeof checkboxCardInputTypes)[number];

export enum Layout {
  SHORT = "short",
  TALL = "tall",
  LEFT = "left",
  CENTER = "center",
}

export type InputSize = "xs" | "sm" | "md";

export interface RedoCheckboxCardProps {
  title: React.ReactNode;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  checked: boolean;
  disabled?: boolean;
  onClick: () => void;
  borderColor?: string;
  inputType?: CheckboxCardInputType;
  inputSize?: InputSize;
  layout?: Layout;
  dataTestId?: string;
}

export const RedoCheckboxCard = memo(function RedoCheckboxCardProps({
  title,
  icon,
  checked,
  disabled,
  onClick,
  inputType,
  inputSize = "md",
  layout = Layout.SHORT,
  children,
  dataTestId,
}: RedoCheckboxCardProps) {
  const titleText =
    typeof title === "string" ? (
      <Text fontSize="md" fontWeight={checked ? "medium" : "regular"}>
        {title}
      </Text>
    ) : (
      title
    );
  const childrenText =
    typeof children === "string" ? (
      <Text color="secondary" fontSize="sm">
        {children}
      </Text>
    ) : (
      children
    );

  const clickHandler = useHandler(() => {
    if (!disabled) {
      onClick();
    }
  });

  return (
    <Flex
      className={classnames(css.outerBorder, {
        [css.checkboxCardSelected]: checked,
        [css.disabled]: disabled,
      })}
      data-testid={dataTestId}
      grow={1}
      m="none"
      onClick={clickHandler}
      p="none"
    >
      <Flex
        className={classnames(css.checkboxCard, {
          [css.checkboxCardSelected]: checked,
          [css.disabled]: disabled,
        })}
        grow={1}
      >
        {layout === Layout.LEFT ? (
          <Flex flexDirection="column" gap="xs" grow={1}>
            <Flex align="flex-start" justify="space-between">
              <Flex align="center" gap="md" grow={1} justify="flex-start">
                <CardInput
                  checked={checked}
                  disabled={disabled}
                  inputSize={inputSize}
                  inputType={inputType}
                />
                {titleText}
              </Flex>
              {icon && <div className={css.cardIcon}>{icon}</div>}
            </Flex>
            <Flex dir="row" flex={1} gap="md" grow={1}>
              <Flex className={inputSizeToSpacerSize[inputSize]} />
              {childrenText}
            </Flex>
          </Flex>
        ) : (
          <Flex
            align={layout === Layout.CENTER ? "center" : "stretch"}
            flexDirection={layout === Layout.SHORT ? "row" : "column"}
            gap={layout === Layout.TALL ? "3xl" : "lg"}
            grow={1}
          >
            <Flex align="flex-start" justify="space-between">
              {icon && <div className={css.cardIcon}>{icon}</div>}
              {layout === Layout.TALL && (
                <CardInput
                  checked={checked}
                  disabled={disabled}
                  inputSize={inputSize}
                  inputType={inputType}
                />
              )}
            </Flex>
            <Flex align="flex-start" dir="column" flex={1} gap="xs">
              {titleText}
              {childrenText}
            </Flex>

            {layout === Layout.SHORT && (
              <CardInput
                checked={checked}
                disabled={disabled}
                inputSize={inputSize}
                inputType={inputType}
              />
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

const CardInput = memo(function CardInput({
  checked,
  inputType,
  inputSize,
  disabled,
}: {
  checked: boolean;
  inputType: CheckboxCardInputType | undefined;
  inputSize: InputSize;
  disabled?: boolean;
}) {
  if (!inputType) {
    return null;
  }
  if (inputType === "checkbox") {
    return (
      <span style={{ pointerEvents: "none" }}>
        <RedoCheckbox
          disabled={disabled}
          setValue={() => {}}
          size={inputSizeToCheckboxSize[inputSize]}
          value={checked}
        />
      </span>
    );
  }
  if (inputType === "radio") {
    return (
      <span style={{ pointerEvents: "none" }}>
        <RedoRadioButton
          disabled={disabled}
          onSelect={() => {}}
          selected={checked}
          size={inputSize}
        />
      </span>
    );
  }
  assertNever(inputType);
});

const inputSizeToCheckboxSize: Record<InputSize, RedoCheckboxSize> = {
  xs: "xs",
  md: "md",
  sm: "sm",
};

const inputSizeToSpacerSize: Record<RedoCheckboxSize, string> = {
  ["xs"]: css.spacerXsmall,
  ["sm"]: css.spacerSmall,
  ["md"]: css.spacerMedium,
};
